import { useDeepMemo, useMount, useThrottledFn } from '@studio/react-utils/hooks';
import { dequal } from 'dequal/lite';
import { PrefKey } from './constants.js';
import type { Formula, HotKeys } from './types.js';
import { deserialize, toFormula } from './utils.js';
import { useNotifier } from '#~/command/hooks.js';
import * as HK from '#~/hot-keys/index.js';
import { usePreferences } from '#~/preferences/hooks.js';

/**
 * Returns the current hot key formula.
 *
 * @returns Hot key formula.
 */
export function useFormula(): Formula {
  const hotKeys = useHotKeys();

  return useDeepMemo(() => toFormula(hotKeys), [hotKeys]);
}

/**
 * Returns the hot keys for the current user.
 *
 * @returns User hot keys.
 */
export function useHotKeys(): HotKeys {
  return usePreferences((µ) => deserialize(µ.getValue(PrefKey)), dequal);
}

/** Registers the observer that watches for hot key commands. */
export const useObserver = (): void => {
  const formula = useFormula();
  const notifyFn = useNotifier();

  const handleKeyDown = useThrottledFn((event: KeyboardEvent) => {
    // Ignore repeat events or events targeted to reserved elements.
    if (event.repeat || HK.isReserved(event)) {
      return;
    }

    // Run the event through the hot key processor.
    const directive = HK.evaluate(formula, event);

    if (!directive) {
      return;
    }

    // Notify the listeners.
    notifyFn(directive);

    event.preventDefault();
  }, 100);

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    document.addEventListener('keydown', handleKeyDown, { signal });

    return () => {
      abortController.abort();
    };
  });
};
